import React, { useState } from 'react';
import Divider from '@material-ui/core/Divider';

import PropTypes from 'prop-types';
import useBreakpoints from '../../../../../hooks/useBreakpoints';
import { Section } from '../types';

import SectionContent from './SectionContent';
import SectionTitle from './SectionTitle';
import LastEdited from './LastEdited';
import {
  Container,
  Separator,
  TitleContainer,
} from './ReadOnlySectionView.styled';
import ScrollBar from '../../../common/ScrollBar';
import {
  ScrollContainer,
  SectionViewContainer,
  ContentContainer,
} from '../Material.styled';
import ReadonlyActionBar from './ReadonlyActionBar';

const ReadOnlySectionView = ({
  section,
  selectedContent,
  onGetSection,
  isLoadingContent,
  isComplete,
  isCompleteLoading,
  submitProgress,
  isStudent,
  educatorOnly = false,
  permissions = [],
  isRbacEnabled = false,
  isSectionPublished = false,
}) => {
  const [isDividerVisible, setIsDividerVisible] = useState(false);
  const { md } = useBreakpoints();

  // Never show mark as complete in educator area
  // Show only if user has progress permission
  // If permissions are available (even when RBAC is disabled), check for progress permission
  // Otherwise, fall back to legacy behavior (show for students when RBAC is disabled)
  const hasPermissionsAvailable =
    permissions && Array.isArray(permissions) && permissions.length > 0;
  const hasProgressPermission = hasPermissionsAvailable
    ? permissions.includes('material:progress')
    : false;
  // Show if: not educator area AND page is published (not hidden, including parent folder) AND (user has progress permission OR (RBAC disabled AND no permissions available AND user is student))
  const shouldShowProgressButton =
    !educatorOnly &&
    isSectionPublished &&
    (isRbacEnabled ? hasProgressPermission : isStudent);

  const isStudentDividerVisible = isStudent && isDividerVisible;
  const StudentReadonlyActionBar = shouldShowProgressButton ? (
    <ReadonlyActionBar
      sectionId={section.id}
      isComplete={isComplete}
      isCompleteLoading={isCompleteLoading}
      submitProgress={submitProgress}
    />
  ) : null;

  const content = (
    <ContentContainer>
      <SectionViewContainer>
        <TitleContainer>
          <SectionTitle value={section.title} />
          <Separator height="16px" />
          <LastEdited
            editedAt={section.updatedAt}
            createdAt={section.createdAt}
          />
        </TitleContainer>
        <SectionContent
          sectionId={section.id}
          content={selectedContent}
          discussionTopic={section.discussionTopic}
          showDiscussion={section.showDiscussion}
          onGetSection={onGetSection}
          isLoadingContent={isLoadingContent}
        />
      </SectionViewContainer>
    </ContentContainer>
  );

  const onScroll = ({ scrollTop }) => {
    setIsDividerVisible(scrollTop > 0);
  };

  if (md) {
    return (
      <Container>
        {StudentReadonlyActionBar}
        {isStudentDividerVisible && <Divider />}
        <ScrollContainer>
          <ScrollBar autoHide onScrollFrame={onScroll}>
            {content}
          </ScrollBar>
        </ScrollContainer>
      </Container>
    );
  }

  return (
    <Container>
      <>
        {StudentReadonlyActionBar}
        {content}
      </>
    </Container>
  );
};

ReadOnlySectionView.propTypes = {
  section: Section.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedContent: PropTypes.object,
  isLoadingContent: PropTypes.bool.isRequired,
  onGetSection: PropTypes.func.isRequired,
  isComplete: PropTypes.bool.isRequired,
  isCompleteLoading: PropTypes.bool.isRequired,
  isStudent: PropTypes.bool.isRequired,
  submitProgress: PropTypes.func.isRequired,
  educatorOnly: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  isSectionPublished: PropTypes.bool,
};

ReadOnlySectionView.defaultProps = {
  selectedContent: null,
  educatorOnly: false,
  permissions: [],
  isRbacEnabled: false,
};

export default ReadOnlySectionView;
